import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { externalActions } from "../ducks/external";

const SKIP_RECAPTCHA = false; // For testing only, should be set to false in production

const RECAPTCHA_TAG_ID = "recaptcha";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? "";

interface RecaptchaWindow extends Window {
  grecaptcha: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
  };
}

declare const window: RecaptchaWindow;

export const useRecaptcha = () => {
  const dispatch = useDispatch();
  const [recaptchaLoaded, setRecaptchaLoaded] = useState<boolean>(false);

  useEffect(() => {
    const recaptchaTag = document.getElementById(RECAPTCHA_TAG_ID);

    if (!recaptchaTag) {
      const head = document.getElementsByTagName("head")[0];
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.id = RECAPTCHA_TAG_ID;
      script.onload = () => {
        toggleRecaptchaBadge(true);
        setRecaptchaLoaded(true);
      };
      script.src =
        "https://www.google.com/recaptcha/api.js?render=" + RECAPTCHA_SITE_KEY;
      head.appendChild(script);
    }

    return () => {
      toggleRecaptchaBadge(false);
      setRecaptchaLoaded(false);
      document.getElementById(RECAPTCHA_TAG_ID)?.remove();
    };
  }, []);

  const toggleRecaptchaBadge = useCallback((show: boolean) => {
    const reacap = document.getElementsByClassName("grecaptcha-badge");
    if (reacap) {
      for (let i = 0; i < reacap.length; i++) {
        const item: any = reacap.item(i);
        item.style.visibility = show ? "visible" : "hidden";
      }
    }
  }, []);

  const checkRecaptcha = useCallback(
    (onSuccess: () => void, onError: () => void) => {
      if (SKIP_RECAPTCHA) return onSuccess();
      if (!recaptchaLoaded) return onError();

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: "submit" })
          .then((token) => {
            dispatch(
              externalActions.checkRecaptcha.started({
                recaptchaToken: token,
                onError,
                onSuccess,
              }),
            );
          });
      });
    },
    [recaptchaLoaded],
  );

  return { checkRecaptcha };
};
